<app-dialog-content-layout
  headerTitle="themeSettings"
  [showFooter]="false"
  (cancel)="close()"
  [showBgImage]="false"
>
  <div class="flex flex-col gap-sm">
    <!-- Language Section -->
    <div class="border border-line p-sm rounded-lg">
      <h3 class="text-sm font-semibold text-gray-text mb-2">
        {{ 'language' | translate }}
      </h3>
      <div
        class="grid gap-3"
        [class.grid-cols-2]="languageOptions().length <= 2"
        [class.grid-cols-3]="languageOptions().length >= 3"
      >
        @for (lang of languageOptions(); track lang.code) {
        <button
          type="button"
          (click)="selectLanguage(lang.code)"
          class="py-2 px-3 rounded-md transition-all duration-200 text-sm cursor-pointer bg-soft-bg text-gray-text font-medium flex items-center justify-between"
          [attr.aria-pressed]="selectedLanguage() === lang.code"
          [attr.aria-label]="lang.name"
        >
          <span>{{ lang.label }}</span>
          <div
            class="w-4 h-4 rounded-full bg-card-gradient flex items-center justify-center transition-opacity duration-150"
            [class.opacity-0]="selectedLanguage() !== lang.code"
          >
            <ic-check
              [height]="'7px'"
              [width]="'7px'"
              [strokeWidth]="'1.5'"
              color="#ffffff"
            ></ic-check>
          </div>
        </button>
        }
      </div>
    </div>

    <!-- Themes Section -->
    <div class="border border-line p-sm rounded-lg">
      <h3 class="text-sm font-semibold text-gray-text mb-xs">
        {{ 'themes' | translate }}
      </h3>
      <div class="grid grid-cols-4 gap-2">
        @for (theme of themes; track theme.id) {
        <button
          type="button"
          (click)="selectTheme(theme.id)"
          class="relative h-8 px-0 rounded-md transition-all duration-200 overflow-visible flex cursor-pointer min-w-0 border-none outline-none"
          [attr.aria-pressed]="selectedTheme() === theme.id"
          [attr.aria-label]="theme.name"
        >
          <div
            [ngStyle]="{ 'background-color': '#' + theme.start }"
            class="w-1/2 h-full rounded-l-md"
          ></div>
          <div
            [ngStyle]="{ 'background-color': '#' + theme.end }"
            class="w-1/2 h-full rounded-r-md"
          ></div>
          @if (selectedTheme() === theme.id) {
          <div
            class="absolute -top-1 -right-0.5 w-3.5 h-3.5 z-10 rounded-full bg-card-gradient flex items-center justify-center"
          >
            <ic-check
              [height]="'6px'"
              [width]="'6px'"
              [strokeWidth]="'1.5'"
              color="#ffffff"
            ></ic-check>
          </div>
          }
        </button>
        }
      </div>
    </div>

    <!-- Navigation Mode Section -->
    <div class="border border-line p-sm rounded-lg">
      <h3 class="text-sm font-semibold text-gray-text mb-2">
        {{ 'navigationMode' | translate }}
      </h3>
      <div class="grid grid-cols-3 gap-3">
        <button
          (click)="selectNavigationMode('bottom-bar')"
          class="py-2 px-3 rounded-md transition-all duration-200 text-sm cursor-pointer bg-soft-bg text-gray-text font-medium flex items-center justify-between"
          [attr.aria-pressed]="
            layoutService.actualNavigationMode() === 'bottom-bar'
          "
        >
          <span>{{ 'bottomBar' | translate }}</span>
          <div
            class="w-4 h-4 rounded-full bg-card-gradient flex items-center justify-center transition-opacity duration-150"
            [class.opacity-0]="
              layoutService.actualNavigationMode() !== 'bottom-bar'
            "
          >
            <ic-check
              [height]="'7px'"
              [width]="'7px'"
              [strokeWidth]="'1.5'"
              color="#ffffff"
            ></ic-check>
          </div>
        </button>
        <button
          (click)="selectNavigationMode('side-menu')"
          class="py-2 px-3 rounded-md transition-all duration-200 text-sm cursor-pointer bg-soft-bg text-gray-text font-medium flex items-center justify-between"
          [attr.aria-pressed]="layoutService.actualNavigationMode() === 'side-menu'"
        >
          <span>{{ 'sideMenu' | translate }}</span>
          <div
            class="w-4 h-4 rounded-full bg-card-gradient flex items-center justify-center transition-opacity duration-150"
            [class.opacity-0]="layoutService.actualNavigationMode() !== 'side-menu'"
          >
            <ic-check
              [height]="'7px'"
              [width]="'7px'"
              [strokeWidth]="'1.5'"
              color="#ffffff"
            ></ic-check>
          </div>
        </button>
        <button
          (click)="selectNavigationMode('top-bar')"
          class="py-2 px-3 rounded-md transition-all duration-200 text-sm cursor-pointer bg-soft-bg text-gray-text font-medium flex items-center justify-between"
          [attr.aria-pressed]="layoutService.actualNavigationMode() === 'top-bar'"
        >
          <span>{{ 'topBar' | translate }}</span>
          <div
            class="w-4 h-4 rounded-full bg-card-gradient flex items-center justify-center transition-opacity duration-150"
            [class.opacity-0]="layoutService.actualNavigationMode() !== 'top-bar'"
          >
            <ic-check
              [height]="'7px'"
              [width]="'7px'"
              [strokeWidth]="'1.5'"
              color="#ffffff"
            ></ic-check>
          </div>
        </button>
      </div>
    </div>

    <!-- Default Module Section -->
    <div class="border border-line p-sm rounded-lg">
      <div class="flex items-center gap-1.5 mb-2">
        <h3 class="text-sm font-semibold text-gray-text">
          {{ 'defaultModule' | translate }}
        </h3>
        <ic-info
          [width]="12"
          [height]="12"
          [startColor]="'var(--color-primary-start-color)'"
          [endColor]="'var(--color-primary-end-color)'"
          [opacity]="0.6"
          [matTooltip]="'defaultModuleDescription' | translate"
          matTooltipPosition="above"
        ></ic-info>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <button
          (click)="setDefaultDashboard('shipments')"
          class="py-2 px-3 rounded-md transition-all duration-200 text-sm cursor-pointer bg-soft-bg text-gray-text font-medium flex items-center justify-between"
          [attr.aria-pressed]="defaultDashboard() === 'shipments'"
        >
          <span>{{ 'shipments' | translate }}</span>
          <div
            class="w-4 h-4 rounded-full bg-card-gradient flex items-center justify-center transition-opacity duration-150"
            [class.opacity-0]="defaultDashboard() !== 'shipments'"
          >
            <ic-check
              [height]="'7px'"
              [width]="'7px'"
              [strokeWidth]="'1.5'"
              color="#ffffff"
            ></ic-check>
          </div>
        </button>
        <button
          (click)="setDefaultDashboard('declarations')"
          class="py-2 px-3 rounded-md transition-all duration-200 text-sm cursor-pointer bg-soft-bg text-gray-text font-medium flex items-center justify-between"
          [attr.aria-pressed]="defaultDashboard() === 'declarations'"
        >
          <span>{{ 'declarations' | translate }}</span>
          <div
            class="w-4 h-4 rounded-full bg-card-gradient flex items-center justify-center transition-opacity duration-150"
            [class.opacity-0]="defaultDashboard() !== 'declarations'"
          >
            <ic-check
              [height]="'7px'"
              [width]="'7px'"
              [strokeWidth]="'1.5'"
              color="#ffffff"
            ></ic-check>
          </div>
        </button>
      </div>
    </div>

    <!-- Module Landing Page Preference -->
    <div class="border border-line p-sm rounded-lg">
      <div class="flex items-center gap-1.5 mb-2">
        <h3 class="text-sm font-semibold text-gray-text">
          {{ 'moduleLandingPage' | translate }}
        </h3>
        <ic-info
          [width]="12"
          [height]="12"
          [startColor]="'var(--color-primary-start-color)'"
          [endColor]="'var(--color-primary-end-color)'"
          [opacity]="0.6"
          [matTooltip]="'moduleLandingPageDescription' | translate"
          matTooltipPosition="above"
        ></ic-info>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <button
          (click)="setModuleLandingPage('dashboard')"
          class="py-2 px-3 rounded-md transition-all duration-200 text-sm cursor-pointer bg-soft-bg text-gray-text font-medium flex items-center justify-between"
          [attr.aria-pressed]="moduleLandingPage() === 'dashboard'"
        >
          <span>{{ 'dashboard' | translate }}</span>
          <div
            class="w-4 h-4 rounded-full bg-card-gradient flex items-center justify-center transition-opacity duration-150"
            [class.opacity-0]="moduleLandingPage() !== 'dashboard'"
          >
            <ic-check
              [height]="'7px'"
              [width]="'7px'"
              [strokeWidth]="'1.5'"
              color="#ffffff"
            ></ic-check>
          </div>
        </button>
        <button
          (click)="setModuleLandingPage('list')"
          class="py-2 px-3 rounded-md transition-all duration-200 text-sm cursor-pointer bg-soft-bg text-gray-text font-medium flex items-center justify-between"
          [attr.aria-pressed]="moduleLandingPage() === 'list'"
        >
          <span>{{ 'list' | translate }}</span>
          <div
            class="w-4 h-4 rounded-full bg-card-gradient flex items-center justify-center transition-opacity duration-150"
            [class.opacity-0]="moduleLandingPage() !== 'list'"
          >
            <ic-check
              [height]="'7px'"
              [width]="'7px'"
              [strokeWidth]="'1.5'"
              color="#ffffff"
            ></ic-check>
          </div>
        </button>
      </div>
    </div>
  </div>
</app-dialog-content-layout>
